import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';

import { PATH_ROUTES } from 'routes/path';
import { Feature, isFeatureEnabled } from 'lib/feature-flags';
import { cn } from 'lib/utils';

import CheckVital from 'assets/images/dashboard/check-vitals.png';
import GenerateReport from 'assets/images/dashboard/generate-report.png';
import InterpretReport from 'assets/images/dashboard/interpret-report.png';
import RiskChecker from 'assets/images/health-risks.png';

export default function AppFeatureLinks() {
  const { t } = useTranslation();

  const links = [
    {
      label: t('check_vitals_biomarkers'),
      labelSm: t('face_scan'),
      img: CheckVital,
      desc: t('check_vitals_biomarkers_desc'),
      href: PATH_ROUTES.START_FACE_SCAN,
      color: '#10A4B1',
      feature: 'FACE_SCAN' as Feature,
    },
    {
      label: t('risk_checker'),
      labelSm: t('risk_checker'),
      img: RiskChecker,
      desc: t('risk_checker_nav_desc'),
      href: PATH_ROUTES.RISK_ANALYSIS,
      color: '#F43F5E',
      feature: 'RISK_CHECKER' as Feature,
    },
    {
      label: t('general_health_report'),
      labelSm: t('health_report'),
      img: GenerateReport,
      desc: t('general_health_report_desc'),
      href: PATH_ROUTES.GENERATE_AI_REPORT,
      color: '#ACB7FA',
      feature: 'AI_REPORT' as Feature,
    },
    {
      label: t('interpret_lab_results'),
      labelSm: t('interpret_lab_results'),
      img: InterpretReport,
      desc: t('interpret_lab_results_desc'),
      href: PATH_ROUTES.GENERATE_LAB_REPORT,
      color: '#4CB7D8',
      feature: 'LAB_REPORT' as Feature,
    },
  ].filter(({ feature }) => isFeatureEnabled(feature));

  if (!links.length) return null;

  return (
    <div
      className={cn(
        'grid shrink-0 h-full min-h-fit gap-x-2 sm:gap-x-4',
        links.length === 1 && 'sm:max-w-xl',
        links.length === 2 && 'sm:grid-cols-2',
        links.length >= 3 && 'sm:grid-cols-2 lg:grid-cols-3'
      )}
    >
      {links.map(({ href, label, img, desc, color, labelSm }) => (
        <Link
          to={href}
          key={href}
          className='relative flex flex-col justify-center flex-1 gap-2 p-3 border border-l-2 shadow-md max-lg:pb-5 rounded-3xl'
          style={{ borderLeftColor: color }}
        >
          <h2 className='hidden text-sm font-semibold leading-tight sm:block sm:text-base'>
            {label}
          </h2>
          <h2 className='text-sm font-semibold leading-tight sm:hidden'>
            {labelSm}
          </h2>

          <div className='relative flex items-center flex-1 w-full gap-4 max-lg:flex-col'>
            <div className='w-14 h-14 shrink-0'>
              <img
                src={img}
                alt={`${label} image`}
                className='object-contain w-full h-full'
              />
            </div>

            <p className='flex-1 hidden text-sm leading-snug 3xl:text-base sm:block sm:pr-6'>
              {desc}
            </p>

            <ChevronRight
              className='absolute hidden w-4 h-4 -translate-y-1/2 top-1/2 right-0 shrink-0 sm:block'
              style={{ color }}
            />
          </div>
        </Link>
      ))}
    </div>
  );
}
